import { useState, useCallback, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { t } from '@/lib/i18n';
import { formatDate, DATE_RANGE_PRESETS, DEFAULT_DATE_RANGE } from '@/lib/widgets/date-ranges';
import type { DateRange } from '@/lib/widgets/types';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
    value: string; // preset key or 'custom'
    customRange?: DateRange;
    onChange: (presetKey: string, customRange?: DateRange) => void;
}

function toInputDate(d: Date): string {
    return d.toISOString().split('T')[0];
}

export function DateRangePicker({ value, customRange, onChange }: DateRangePickerProps) {
    const [open, setOpen] = useState(false);
    const [localStart, setLocalStart] = useState(customRange?.start ?? toInputDate(new Date(Date.now() - 30 * 86400000)));
    const [localEnd, setLocalEnd] = useState(customRange?.end ?? toInputDate(new Date()));

    const activePreset = DATE_RANGE_PRESETS.find((p) => p.key === value);

    const displayLabel = useMemo(() => {
        if (activePreset) return t(activePreset.labelKey);
        if (customRange) return `${customRange.start} – ${customRange.end}`;
        return t('dateRanges.custom');
    }, [activePreset, customRange]);

    const handlePresetClick = useCallback(
        (presetKey: string) => {
            onChange(presetKey);
            setOpen(false);
        },
        [onChange],
    );

    const handleCustomApply = useCallback(() => {
        if (localStart && localEnd && localStart <= localEnd) {
            onChange('custom', { start: localStart, end: localEnd });
            setOpen(false);
        }
    }, [localStart, localEnd, onChange]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                        'w-[220px] justify-between text-xs h-9',
                        value === 'custom' && 'border-primary text-primary',
                    )}
                >
                    <span className="flex items-center gap-2">
                        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate">{displayLabel}</span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-3" align="end">
                <div className="space-y-3">
                    {/* Presets */}
                    <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">{t('dateRanges.presets')}</p>
                        <div className="grid grid-cols-2 gap-1">
                            {DATE_RANGE_PRESETS.map((preset) => (
                                <Button
                                    key={preset.key}
                                    variant={value === preset.key ? 'secondary' : 'ghost'}
                                    size="sm"
                                    className="h-7 justify-start text-xs px-2"
                                    onClick={() => handlePresetClick(preset.key)}
                                >
                                    {t(preset.labelKey)}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Custom range */}
                    <div className="space-y-2 pt-2 border-t">
                        <p className="text-xs font-medium text-muted-foreground">{t('dateRanges.customRange')}</p>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={localStart}
                                max={localEnd}
                                onChange={(e) => setLocalStart(e.target.value)}
                                className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            />
                            <span className="text-xs text-muted-foreground">–</span>
                            <input
                                type="date"
                                value={localEnd}
                                min={localStart}
                                onChange={(e) => setLocalEnd(e.target.value)}
                                className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            />
                        </div>
                        <Button
                            size="sm"
                            className="w-full h-8 text-xs"
                            disabled={!localStart || !localEnd || localStart > localEnd}
                            onClick={handleCustomApply}
                        >
                            {t('dateRanges.apply')}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
